import { IconName } from "../components-v2/iconMapping";
import { TMessage } from "../i18n/tt";
import { LocalDateFieldModel } from "../common/commonForms";
import { TMessageKey } from "../i18n/types.gen";
import { Amount } from "../common/common";
import { compact, omit, trim } from "lodash";

export type AutotextOption = {
  type: AutotextCategory;
  icon: IconName;
  label: TMessage;
  categoryTag: TMessage;
  dialogTitle: TMessage;
};

export enum AutotextCategory {
  companyInformation = "companyInformation",
  capitalIncrease = "capitalIncrease",
  shareholding = "shareholding",
  subscription = "subscription",
}

const autotextIcons: Record<AutotextCategory, IconName> = {
  [AutotextCategory.companyInformation]: "buildings",
  [AutotextCategory.capitalIncrease]: "file-plus",
  [AutotextCategory.shareholding]: "user-square",
  [AutotextCategory.subscription]: "user-circle-plus",
};

const autotextLabels: Record<AutotextCategory, TMessage> = {
  [AutotextCategory.companyInformation]: { key: "ai.actions.autotext.companyInformation" },
  [AutotextCategory.capitalIncrease]: { key: "ai.actions.autotext.capitalIncrease" },
  [AutotextCategory.shareholding]: { key: "ai.actions.autotext.shareholding" },
  [AutotextCategory.subscription]: { key: "ai.actions.autotext.subscription" },
};

const autotextCategories: Record<AutotextCategory, TMessage> = {
  [AutotextCategory.companyInformation]: { key: "ai.actions.autotext.categories.general" },
  [AutotextCategory.capitalIncrease]: { key: "ai.actions.autotext.categories.general" },
  [AutotextCategory.shareholding]: { key: "ai.actions.autotext.categories.personalized" },
  [AutotextCategory.subscription]: { key: "ai.actions.autotext.categories.personalized" },
};

export const autotextDialogTitles: Record<AutotextCategory, TMessage> = {
  [AutotextCategory.companyInformation]: { key: "ai.actions.autotext.companyInformation.title" },
  [AutotextCategory.capitalIncrease]: { key: "ai.actions.autotext.capitalIncrease.title" },
  [AutotextCategory.shareholding]: { key: "ai.actions.autotext.shareholding.title" },
  [AutotextCategory.subscription]: { key: "ai.actions.autotext.subscription.title" },
};

export const autotexts: AutotextOption[] = Object.values(AutotextCategory).map((type) => ({
  type,
  icon: autotextIcons[type],
  label: autotextLabels[type],
  categoryTag: autotextCategories[type],
  dialogTitle: autotextDialogTitles[type],
}));

export type AutotextVariable = {
  label: TMessage;
  placeholderValue: DataRequest["type"];
};

const autotextVariableLabels: { [key in DataRequest["type"]]: TMessageKey } = {
  companyName: "ai.autotext.company-name",
  taxAssessmentPerShare: "dbot.key-figure.taxAssessmentValuePerShare",
  nominalValuePerShare: "dbot.key-figure.nominalValuePerShare",
  fairValuePerShare: "dbot.key-figure.fairValuePerShare",
  totalShareCapital: "dbot.key-figure.totalShareCapital",
  totalNumberOfShares: "dbot.key-figure.totalNumberOfShares",
  numberOfShareholders: "dbot.key-figure.numberOfShareholders",
  shareCapitalIncreaseMethod: "ai.autotext.shareCapitalIncreaseMethod",
  shareCapitalIncreaseTotalAmount: "ai.autotext.shareCapitalIncreaseTotalAmount",
  shareCapitalIncreaseSubscriptionPrice: "ai.autotext.shareCapitalIncreaseSubscriptionPrice",
  shareCapitalIncreaseTotalNewShares: "ai.autotext.shareCapitalIncreaseTotalNewShares",
  shareCapitalIncreaseShareClasses: "ai.autotext.shareCapitalIncreaseShareClasses",
  shareholderName: "ai.autotext.shareholderName",
  shareholderTotalNumberOfShares: "ai.autotext.shareholderTotalNumberOfShares",
  shareholderTotalCostPrice: "ai.autotext.shareholderTotalCostPrice",
  shareholderLockUpEndDate: "ai.autotext.shareholderLockUpEndDate",
  shareholderLoanAmount: "ai.autotext.shareholderLoanAmount",
  shareholderCommittedTotal: "ai.autotext.shareholderCommittedTotal",
  shareholderCommittedDrawn: "ai.autotext.shareholderCommittedDrawn",
  shareholderCommittedDistributed: "ai.autotext.shareholderCommittedDistributed",
  shareholderCommittedRemaining: "ai.autotext.shareholderCommittedRemaining",
  shareholderNumberOfPledgedShares: "ai.autotext.shareholderNumberOfPledgedShares",
  subscriberTotalAmount: "ai.autotext.subscriberTotalAmount",
  subscriberTotalNewShares: "ai.autotext.subscriberTotalNewShares",
  subscriberPostRoundTotalShares: "ai.autotext.subscriberPostRoundTotalShares",
  subscriberPostRoundCommitted: "ai.autotext.subscriberPostRoundCommitted",
};

const companyInfoVariables: DataRequest["type"][] = [
  "companyName",
  "taxAssessmentPerShare",
  "nominalValuePerShare",
  "fairValuePerShare",
  "totalShareCapital",
  "totalNumberOfShares",
  "numberOfShareholders",
];

const shareCapitalIncreaseVariables: DataRequest["type"][] = [
  "shareCapitalIncreaseMethod",
  "shareCapitalIncreaseTotalAmount",
  "shareCapitalIncreaseSubscriptionPrice",
  "shareCapitalIncreaseTotalNewShares",
  "shareCapitalIncreaseShareClasses",
];

const shareholdingVariables: DataRequest["type"][] = [
  "shareholderName",
  "shareholderTotalNumberOfShares",
  "shareholderTotalCostPrice",
  "shareholderLockUpEndDate",
  "shareholderLoanAmount",
  "shareholderCommittedTotal",
  "shareholderCommittedDrawn",
  "shareholderCommittedDistributed",
  "shareholderCommittedRemaining",
  "shareholderNumberOfPledgedShares",
];

const subscriptionVariables: DataRequest["type"][] = [
  "subscriberTotalAmount",
  "subscriberTotalNewShares",
  "subscriberPostRoundTotalShares",
  "subscriberPostRoundCommitted",
];

export const autotextVariables: Record<AutotextCategory, AutotextVariable[]> = {
  [AutotextCategory.companyInformation]: toVariables(companyInfoVariables),
  [AutotextCategory.capitalIncrease]: toVariables(shareCapitalIncreaseVariables),
  [AutotextCategory.shareholding]: toVariables(shareholdingVariables),
  [AutotextCategory.subscription]: toVariables(subscriptionVariables),
};

function toVariables(vars: DataRequest["type"][]): AutotextVariable[] {
  return vars.map((v) => ({
    label: { key: autotextVariableLabels[v] },
    placeholderValue: v,
  }));
}

export type SelectionAwareDataSource = {
  getData(req: DataRequest): DataResponse | undefined;
  includeShareCapital: boolean;
};

export type AutotextProps = {
  autotextOption: AutotextOption;
  perDate?: LocalDateFieldModel;
  onCancel: () => void;
  onSelectedVariable: (variableName: DataRequest["type"], perDate?: string) => void;
  dataProvider?: SelectionAwareDataSource;
};

export const toDataRequest = (
  option: AutotextOption,
  type: DataRequest["type"],
  perDate?: string,
): DataRequest => {
  switch (option.type) {
    case AutotextCategory.capitalIncrease:
    case AutotextCategory.subscription:
      return { type } as DataRequest;
    case AutotextCategory.shareholding:
    case AutotextCategory.companyInformation:
      return { type, perDate } as DataRequest;
  }
};

export type CompanyName = {
  type: "companyName";
  perDate?: string;
};

export type TaxAssessmentPerShareCompanyInformation = {
  type: "taxAssessmentPerShare";
  perDate?: string;
};

export type NominalValuePerShare = {
  type: "nominalValuePerShare";
  perDate?: string;
};

export type FairValuePerShare = {
  type: "fairValuePerShare";
  perDate?: string;
};

export type TotalShareCapital = {
  type: "totalShareCapital";
  perDate?: string;
};

export type TotalNumberOfShares = {
  type: "totalNumberOfShares";
  perDate?: string;
};

export type NumberOfShareholders = {
  type: "numberOfShareholders";
  perDate?: string;
};

export type ShareCapitalIncreaseMethod = {
  type: "shareCapitalIncreaseMethod";
};

export type ShareCapitalIncreaseTotalAmount = {
  type: "shareCapitalIncreaseTotalAmount";
};

export type ShareCapitalIncreaseSubscriptionPrice = {
  type: "shareCapitalIncreaseSubscriptionPrice";
};

export type ShareCapitalIncreaseTotalNewShares = {
  type: "shareCapitalIncreaseTotalNewShares";
};

export type ShareCapitalIncreaseShareClasses = {
  type: "shareCapitalIncreaseShareClasses";
};

export const taxAssessmentPerShare: TaxAssessmentPerShareCompanyInformation = {
  type: "taxAssessmentPerShare",
};

export type ShareholderName = {
  type: "shareholderName";
};

export type ShareholderTotalNumberOfShares = {
  type: "shareholderTotalNumberOfShares";
};

export type ShareholderTotalCostPrice = {
  type: "shareholderTotalCostPrice";
};

export type ShareholderLockUpEndDate = {
  type: "shareholderLockUpEndDate";
};

export type ShareholderLoanAmount = {
  type: "shareholderLoanAmount";
};

export type ShareholderCommittedTotal = {
  type: "shareholderCommittedTotal";
};

export type ShareholderCommittedDrawn = {
  type: "shareholderCommittedDrawn";
};

export type ShareholderCommittedDistributed = {
  type: "shareholderCommittedDistributed";
};

export type ShareholderCommittedRemaining = {
  type: "shareholderCommittedRemaining";
};

export type ShareholderNumberOfPledgedShares = {
  type: "shareholderNumberOfPledgedShares";
};

export type SubscriberTotalAmount = {
  type: "subscriberTotalAmount";
};

export type SubscriberTotalNewShares = {
  type: "subscriberTotalNewShares";
};

export type SubscriberPostRoundTotalShares = {
  type: "subscriberPostRoundTotalShares";
};

export type SubscriberPostRoundCommitted = {
  type: "subscriberPostRoundCommitted";
};

export type DataRequest =
  //companyinformation
  | CompanyName
  | TaxAssessmentPerShareCompanyInformation
  | NominalValuePerShare
  | FairValuePerShare
  | TotalShareCapital
  | TotalNumberOfShares
  | NumberOfShareholders

  //capital increase
  | ShareCapitalIncreaseMethod
  | ShareCapitalIncreaseTotalAmount
  | ShareCapitalIncreaseSubscriptionPrice
  | ShareCapitalIncreaseTotalNewShares
  | ShareCapitalIncreaseShareClasses

  //shareholding
  | ShareholderName
  | ShareholderTotalNumberOfShares
  | ShareholderTotalCostPrice
  | ShareholderLockUpEndDate
  | ShareholderLoanAmount
  | ShareholderCommittedTotal
  | ShareholderCommittedDrawn
  | ShareholderCommittedDistributed
  | ShareholderCommittedRemaining
  | ShareholderNumberOfPledgedShares

  //subscription
  | SubscriberTotalAmount
  | SubscriberTotalNewShares
  | SubscriberPostRoundTotalShares
  | SubscriberPostRoundCommitted;

export const dataRequestTypes: string[] = [
  ...companyInfoVariables,
  ...shareCapitalIncreaseVariables,
  ...shareholdingVariables,
  ...subscriptionVariables,
];

export type DataResponse = {
  type: "companyInformation";
  companyName: string;
  taxAssessmentValue?: Amount;
};

export function markdownToDataRequest(text: string): DataRequest {
  const [type, ...parts] = trim(text, "{}").split(";");
  const params = Object.fromEntries(parts.map((p) => p.split("=")));
  return {
    type,
    ...params,
  } as DataRequest;
}

export function dataRequestToMarkdown(dataRequest: DataRequest): string {
  const name = dataRequest.type;
  const params = Object.entries(omit(dataRequest, "type"))
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${v}`)
    .join(";");

  return `{{${compact([name, params]).join(";")}}}`;
}

export function renderDataRequest(dataRequest: DataRequest): string {
  const parts: string[] = [dataRequest.type];
  if ("perDate" in dataRequest) {
    parts.push(dataRequest.perDate ?? "");
  }
  return `${compact(parts).join(";")}`;
}
